// StorePicker + StoreDraftPanel — the checkout-path store association UI
// (device-count unit, Task 9, design §6/§6.2).
//
// StorePicker is the rule-66 picker: a FILTERABLE list of the customer's own
// stores from the live GET /me/stores, each option showing the LABEL (name +
// address) and submitting the ID via onPick — never a raw FK field. Quick-add
// (POST /me/stores) renders INLINE when the customer has no stores yet, and
// stays reachable at any time via "Add a new store", because "create" is a
// deliberate choice in every flow this component serves (design §6.2).
//
// StoreDraftPanel is the post-login reconciliation of a pre-login staged
// store draft (ruling 9(a)/§6.2): it shows the recall-favored candidate
// matches and lets the customer CONFIRM — never auto-associates, never
// auto-creates. The cart mutation itself stays with the caller (CartView owns
// the cart context); the panel owns fetching stores, computing candidates,
// and the create-then-apply sequence.
//
// Inline styles only — no Tailwind. Every <button> rendered here carries an
// explicit type (rule 73): option/candidate/toggle/try-again buttons are
// type="button" and sit OUTSIDE any <form>; the shared field form's submit
// button is the only type="submit" and lives inside its own <form>
// onSubmit — the StoreForm.tsx convention.

import { useCallback, useEffect, useId, useMemo, useState, type FormEvent } from "react";
import {
  useEmbedTokens,
  EmbedBanner,
  EmbedButton,
  EmbedField,
  EmbedInput,
  EmbedSelect,
  EmbedSpinner,
  EmbedText,
} from "../../embed-shared/primitives";
import { ApiError } from "../../embed-shared/api-client";
import type { AddLineResult } from "../../embed-shared/cart-context";
import { createStore, listStores, type CreateStoreRequest, type CustomerStoreWire } from "./store-api";
import { findStoreCandidates, type StagedStoreDraft } from "./store-staging";
import { US_STATES } from "./StoreForm";

/** One-line address for a store label: "123 Main St, Santa Cruz, CA 95060". */
export function storeAddressLine(store: CustomerStoreWire): string {
  return [store.line1, store.line2, store.city, store.state, store.postal_code]
    .filter((part) => typeof part === "string" && part.trim().length > 0)
    .join(", ");
}

/** The raw field values both capture forms produce (name pre-trimmed; blank
 *  optional fields dropped, matching StoreForm.tsx's convention). */
export interface StoreFields {
  name: string;
  line1?: string;
  city?: string;
  state?: string;
  postal_code?: string;
}

interface StoreFieldsFormProps {
  submitLabel: string;
  submitting: boolean;
  /** API-level error from the caller (create conflicts etc.), shown above the fields. */
  error: string | null;
  /** Receives validated fields — the caller decides what happens next. */
  onSubmit: (fields: StoreFields) => void;
  onCancel?: () => void;
}

/**
 * Shared compact name+address capture. Used in two flows with different
 * verbs (quick-add POSTs /me/stores; the pre-login draft STAGES the values
 * client-side), so it owns ONLY the fields + local name validation — never
 * the submit side effect.
 */
function StoreFieldsForm({ submitLabel, submitting, error, onSubmit, onCancel }: StoreFieldsFormProps) {
  // useId, not fixed literals: TWO instances can be mounted at once (a
  // per-line picker and the reconciliation panel's "all stores" picker) —
  // fixed ids would duplicate and mis-associate every label.
  const idBase = useId();
  const fieldId = (name: string) => `${idBase}-${name}`;
  const [name, setName] = useState("");
  const [line1, setLine1] = useState("");
  const [city, setCity] = useState("");
  const [stateField, setStateField] = useState("");
  const [postal, setPostal] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setNameError("Store name is required.");
      return;
    }
    onSubmit({
      name: name.trim(), // §70: trim, never lowercase
      line1: line1.trim() || undefined,
      city: city.trim() || undefined,
      state: stateField.trim() || undefined,
      postal_code: postal.trim() || undefined,
    });
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      {error && (
        <div style={{ marginBottom: 8 }}>
          <EmbedBanner tone="error">{error}</EmbedBanner>
        </div>
      )}
      <EmbedField label="Store name" htmlFor={fieldId("name")} required error={nameError ?? undefined}>
        <EmbedInput
          id={fieldId("name")}
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (nameError) setNameError(null);
          }}
          aria-required="true"
          aria-invalid={!!nameError}
          invalid={!!nameError}
        />
      </EmbedField>
      <EmbedField label="Address line 1" htmlFor={fieldId("line1")}>
        <EmbedInput
          id={fieldId("line1")}
          name="line1"
          autoComplete="address-line1"
          type="text"
          value={line1}
          onChange={(e) => setLine1(e.target.value)}
        />
      </EmbedField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 8 }}>
        <EmbedField label="City" htmlFor={fieldId("city")}>
          <EmbedInput
            id={fieldId("city")}
            name="city"
            autoComplete="address-level2"
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
        </EmbedField>
        <EmbedField label="State" htmlFor={fieldId("state")}>
          <EmbedSelect
            id={fieldId("state")}
            name="state"
            autoComplete="address-level1"
            value={stateField}
            onChange={(e) => setStateField(e.target.value)}
          >
            <option value="">Select state</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </EmbedSelect>
        </EmbedField>
      </div>
      <div style={{ maxWidth: 200 }}>
        <EmbedField label="ZIP code" htmlFor={fieldId("zip")}>
          <EmbedInput
            id={fieldId("zip")}
            name="postal_code"
            autoComplete="postal-code"
            type="text"
            inputMode="numeric"
            value={postal}
            onChange={(e) => setPostal(e.target.value)}
          />
        </EmbedField>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <EmbedButton type="submit" variant="primary" disabled={submitting} loading={submitting}>
          {submitting ? "Saving..." : submitLabel}
        </EmbedButton>
        {onCancel && (
          <EmbedButton type="button" variant="secondary" disabled={submitting} onClick={onCancel}>
            Cancel
          </EmbedButton>
        )}
      </div>
    </form>
  );
}

/**
 * Pre-login store capture (design §6.2 ruling 9(a)): collects the store the
 * order is for and hands the values to the caller, who STAGES them
 * client-side via store-staging — no API call happens while signed out.
 */
export function StoreDraftForm({
  onCapture,
  onCancel,
}: {
  onCapture: (fields: StoreFields) => void;
  onCancel?: () => void;
}) {
  return (
    <div>
      <p style={{ margin: "0 0 8px 0" }}>
        <EmbedText tone="secondary">
          This product bills per store. Tell us which store it&rsquo;s for — we&rsquo;ll confirm it when you sign in.
        </EmbedText>
      </p>
      <StoreFieldsForm
        submitLabel="Save store"
        submitting={false}
        error={null}
        onSubmit={onCapture}
        onCancel={onCancel}
      />
    </div>
  );
}

export interface StorePickerProps {
  /** Heading copy; defaults to "Choose a store". */
  title?: string;
  /** Called with the CHOSEN store — an existing one or one just created via
   *  quick-add. The store's ID is what callers must submit, never the name. */
  onPick: (store: CustomerStoreWire) => void;
  onCancel?: () => void;
}

export function StorePicker({ title = "Choose a store", onPick, onCancel }: StorePickerProps) {
  const t = useEmbedTokens();
  const filterId = useId(); // two pickers can be mounted at once — see StoreFieldsForm
  const [stores, setStores] = useState<CustomerStoreWire[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    setStores(null);
    try {
      setStores(await listStores());
    } catch {
      setLoadError("We couldn't load your stores. Please try again.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Quick-add (rule 8: sequential awaits — create, THEN pick the result):
  // onPick receives the SERVER's persisted store, so the caller submits the
  // created id, never a client guess.
  async function handleQuickAdd(fields: StoreFields) {
    setCreating(true);
    setCreateError(null);
    try {
      const created = await createStore(fields);
      onPick(created);
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : "We couldn't add that store. Please try again.");
    } finally {
      setCreating(false);
    }
  }

  const normalizedFilter = filter.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!stores) return [];
    if (!normalizedFilter) return stores;
    return stores.filter((s) =>
      [s.name, s.line1, s.city, s.state, s.postal_code]
        .filter(Boolean)
        .some((field) => (field as string).toLowerCase().includes(normalizedFilter)),
    );
  }, [stores, normalizedFilter]);

  if (loadError) {
    return (
      <div style={{ padding: t.spacing.sm + " 0" }}>
        <EmbedBanner tone="error">{loadError}</EmbedBanner>
        <div style={{ marginTop: 8 }}>
          <EmbedButton type="button" variant="secondary" size="sm" onClick={() => void load()}>
            Try again
          </EmbedButton>
        </div>
      </div>
    );
  }

  if (stores === null) {
    return (
      <p role="status" style={{ display: "flex", alignItems: "center", gap: 8, margin: "8px 0", color: t.colors.textSecondary }}>
        <EmbedSpinner />
        <span>Loading stores...</span>
      </p>
    );
  }

  // No stores yet → the quick-add form IS the picker (design §6: "quick-add
  // inline"). Once stores exist, quick-add stays one deliberate click away.
  const showForm = showQuickAdd || stores.length === 0;

  return (
    <div style={{ padding: `${t.spacing.sm}px 0` }}>
      <h4 style={{ fontSize: t.fontSize.md, fontWeight: 700, color: t.colors.text, margin: `0 0 ${t.spacing.xs}px 0` }}>
        {title}
      </h4>
      {showForm ? (
        <>
          {stores.length === 0 && (
            <p style={{ margin: "0 0 8px 0" }}>
              <EmbedText tone="secondary">
                You haven&rsquo;t added any stores yet — add one to continue.
              </EmbedText>
            </p>
          )}
          <StoreFieldsForm
            submitLabel="Add store"
            submitting={creating}
            error={createError}
            onSubmit={handleQuickAdd}
            // With stores to fall back to, Cancel collapses the inline form
            // back to the list. With ZERO stores there is no list — the
            // caller's onCancel IS the dismissal (Task 10, Task-9 review
            // finding (c): a no-stores customer on the PDP was trapped in
            // this form with no way out).
            onCancel={stores.length > 0 ? () => setShowQuickAdd(false) : onCancel}
          />
        </>
      ) : (
        <>
          <EmbedField label="Filter stores" htmlFor={filterId}>
            <EmbedInput
              id={filterId}
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Name, city, or ZIP"
            />
          </EmbedField>
          {filtered.length === 0 ? (
            <p>
              <EmbedText tone="secondary">No stores match that filter.</EmbedText>
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
              {filtered.map((store) => (
                <button
                  key={store.id}
                  type="button"
                  onClick={() => onPick(store)}
                  style={{
                    textAlign: "left",
                    padding: 8,
                    borderRadius: t.borderRadius,
                    border: `1px solid ${t.colors.border}`,
                    background: "transparent",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ display: "block", fontSize: t.fontSize.sm, fontWeight: 600, color: t.colors.text }}>
                    {store.name}
                  </span>
                  {storeAddressLine(store) && (
                    <span style={{ display: "block", fontSize: t.fontSize.xs, color: t.colors.textSecondary }}>
                      {storeAddressLine(store)}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <EmbedButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setShowQuickAdd(true)}
            >
              Add a new store
            </EmbedButton>
            {onCancel && (
              <EmbedButton type="button" variant="ghost" size="sm" onClick={onCancel}>
                Cancel
              </EmbedButton>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export interface StoreDraftPanelProps {
  draft: StagedStoreDraft;
  /**
   * Applies the chosen/created store to the cart line the draft belongs to.
   * Resolves the explicit outcome — the panel stays mounted on failure.
   */
  onApplyStore: (store: CustomerStoreWire) => Promise<AddLineResult>;
  /** Called once, after a SUCCESSFUL apply — the caller clears the draft. */
  onDone: () => void;
}

function draftToCreateRequest(draft: StagedStoreDraft): CreateStoreRequest {
  return {
    name: draft.name,
    line1: draft.line1,
    line2: draft.line2,
    city: draft.city,
    state: draft.state,
    postal_code: draft.postal_code,
    country: draft.country,
  };
}

/**
 * Post-login reconciliation (design §6.2): SHOW the candidate, let the
 * customer confirm. The predicate (findStoreCandidates) deliberately favours
 * RECALL over precision — confirmation is what discriminates, so a missed
 * candidate (→ duplicate store) is the expensive error, not a weak extra
 * candidate. "Create as a new store" is always offered as its own explicit
 * button, never a silent default — even when no candidate surfaced.
 */
export function StoreDraftPanel({ draft, onApplyStore, onDone }: StoreDraftPanelProps) {
  const t = useEmbedTokens();
  const [stores, setStores] = useState<CustomerStoreWire[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    setStores(null);
    try {
      setStores(await listStores());
    } catch {
      setLoadError("We couldn't load your stores. Please try again.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const candidates = useMemo(
    () => (stores ? findStoreCandidates(draft, stores) : []),
    [stores, draft],
  );

  async function handleConfirm(store: CustomerStoreWire) {
    setBusy(true);
    setActionError(null);
    try {
      const result = await onApplyStore(store);
      if (result.ok) {
        onDone();
        return;
      }
      setActionError(result.error ?? "We couldn't save that store. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  // Deliberate create (rule 8 — sequential awaits): create the store from the
  // STAGED fields, then apply the CREATED store's server id to the line.
  async function handleCreate() {
    setBusy(true);
    setActionError(null);
    try {
      const created = await createStore(draftToCreateRequest(draft));
      const result = await onApplyStore(created);
      if (result.ok) {
        onDone();
        return;
      }
      setActionError(result.error ?? "We couldn't save that store. Please try again.");
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "We couldn't create that store. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  const draftAddress = [draft.line1, draft.city, draft.state, draft.postal_code]
    .filter(Boolean)
    .join(", ");

  return (
    <section
      aria-label="Confirm your store"
      style={{
        border: `1px solid ${t.colors.borderSubtle}`,
        borderRadius: t.borderRadius,
        padding: t.spacing.md,
        marginBottom: t.spacing.md,
      }}
    >
      <h4 style={{ fontSize: t.fontSize.md, fontWeight: 700, color: t.colors.text, margin: `0 0 ${t.spacing.xs}px 0` }}>
        Confirm your store
      </h4>
      <p style={{ margin: `0 0 ${t.spacing.sm}px 0`, fontSize: t.fontSize.sm, color: t.colors.text }}>
        You told us this order is for <strong>{draft.name}</strong>
        {draftAddress ? ` — ${draftAddress}` : ""}.
      </p>
      {loadError ? (
        <>
          <EmbedBanner tone="error">{loadError}</EmbedBanner>
          <div style={{ marginTop: 8 }}>
            <EmbedButton type="button" variant="secondary" size="sm" onClick={() => void load()}>
              Try again
            </EmbedButton>
          </div>
        </>
      ) : stores === null ? (
        <p role="status" style={{ display: "flex", alignItems: "center", gap: 8, color: t.colors.textSecondary }}>
          <EmbedSpinner />
          <span>Loading your stores...</span>
        </p>
      ) : (
        <>
          {candidates.length > 0 ? (
            <>
              <p style={{ margin: `0 0 ${t.spacing.xs}px 0` }}>
                <EmbedText tone="secondary">Is it one of these?</EmbedText>
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
                {candidates.map((store) => (
                  <button
                    key={store.id}
                    type="button"
                    disabled={busy}
                    onClick={() => void handleConfirm(store)}
                    style={{
                      textAlign: "left",
                      padding: 8,
                      borderRadius: t.borderRadius,
                      border: `1px solid ${t.colors.border}`,
                      background: "transparent",
                      cursor: busy ? "not-allowed" : "pointer",
                    }}
                  >
                    <span style={{ display: "block", fontSize: t.fontSize.sm, fontWeight: 600, color: t.colors.text }}>
                      {store.name}
                    </span>
                    {storeAddressLine(store) && (
                      <span style={{ display: "block", fontSize: t.fontSize.xs, color: t.colors.textSecondary }}>
                        {storeAddressLine(store)}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <p style={{ margin: `0 0 ${t.spacing.xs}px 0` }}>
              <EmbedText tone="secondary">
                None of your existing stores look like this one.
              </EmbedText>
            </p>
          )}
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <EmbedButton
              type="button"
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? "Hide all my stores" : "Show all my stores"}
            </EmbedButton>
            <EmbedButton
              type="button"
              variant="primary"
              size="sm"
              disabled={busy}
              loading={busy}
              onClick={() => void handleCreate()}
            >
              Create &ldquo;{draft.name}&rdquo; as a new store
            </EmbedButton>
          </div>
          {showAll && <StorePicker title="All your stores" onPick={(store) => void handleConfirm(store)} />}
        </>
      )}
      {actionError && (
        <div style={{ marginTop: 8 }}>
          <EmbedBanner tone="error">{actionError}</EmbedBanner>
        </div>
      )}
    </section>
  );
}
